import { FunctionComponent, useEffect, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  ToastType,
  useToast,
} from "../../../../context_providers/toast/toast_ctx";
import {
  defaultNPD,
  InwardItemModel,
  NPDModel,
  NPDStatus,
} from "../../models/npd_model";
import { fetchGet, fetchPost, fetchPut } from "../../../../service/api_client";
import { urls } from "../../../../utils/urls";
import ModalDialog from "../../../../ui/new_components/common/modal";
import InwardProductComponent from "./inward_product_component";
import Papa from "papaparse";
import "../../../../styles/responsive_shrink.css";

interface NPDProps {}

const NPDInwardScreen: FunctionComponent<NPDProps> = () => {
  const { id } = useParams<{ id: string }>();
  const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/npd`;
  const [, setLoading] = useState(false);
  const [addNewId1, setAddNewId1] = useState(Math.random() * 1000);
  const { showToast } = useToast();
  const [data, setData] = useState<NPDModel>({
    ...defaultNPD,
  });
  const [showNonExistItemsModal, setShowNonExistItemsModal] = useState(false);
  const [nonExistItems, setNonExistItems] = useState<string[]>([]);
  const [screen, setScreen] = useState("inward");
  const navigate = useHistory();
  const existingInwardItems = data?.inward_items || [];

  useEffect(() => {
    if (id) getVoucher(id);
  }, [id]);

  const getVoucher = async (id: string) => {
    setLoading(true);
    try {
      const res = await fetchGet(url + "/" + id);
      if (res.success) {
        setData((prevData) => ({
          ...prevData,
          ...res.data,
        }));
      } else {
        showToast({ type: ToastType.error, text: res.error });
      }
    } catch (error) {
      showToast({ type: ToastType.error, text: "Failed to fetch voucher" });
    }
    setLoading(false);
  };

  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click(); // Programmatically trigger file input
    }
  };

  const handleDownloadTemplate = () => {
    const headers = ["Warehouse", "Item Code", "Device ID"];

    const csvContent = [headers]
      .map((e) => e.map((v) => `"${v}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "npd_inward_template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCSVUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validMimeTypes = ["text/csv", "application/vnd.ms-excel"];
    const fileExtension = file.name.split(".").pop()?.toLowerCase();

    if (!validMimeTypes.includes(file.type) && fileExtension !== "csv") {
      showToast({ type: ToastType.error, text: "Only CSV files are allowed." });
      event.target.value = "";
      return;
    }

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        const parsedRows = results.data as Array<Record<string, string>>;

        // Check required headers
        const requiredHeaders = ["Warehouse", "Item Code", "Device ID"];
        const missingHeaders = requiredHeaders.filter(
          (h) => !(h in (parsedRows[0] || {})),
        );

        if (missingHeaders.length > 0) {
          showToast({
            type: ToastType.error,
            text: `Missing columns: ${missingHeaders.join(", ")}`,
          });
          return;
        }

        // Trim and map the data
        const formattedData = parsedRows.map((row) => ({
          warehouse_name: row["Warehouse"]?.trim(),
          item_code: row["Item Code"]?.trim(),
          device_id: row["Device ID"]?.trim(),
        }));

        // Check for duplicate device IDs
        const deviceIdCount = new Map<string, number>();
        for (const row of formattedData) {
          const id = row.device_id;
          deviceIdCount.set(id, (deviceIdCount.get(id) || 0) + 1);
        }

        const duplicateIds = Array.from(deviceIdCount.entries())
          .filter(([_, count]) => count > 1)
          .map(([id]) => id);

        if (duplicateIds.length > 0) {
          showToast({
            type: ToastType.error,
            text: `Duplicate Device IDs found: ${duplicateIds.join(", ")}`,
          });
          return;
        }

        // All good: use the formattedData
        const upload_url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/npd/import-devices`;

        try {
          const res = await fetchPost(upload_url, formattedData);

          if (res.success) {
            setData((o: NPDModel) => ({
              ...o,
              inward_items: [...(o.inward_items || []), ...res.data],
            }));
            showToast({ type: ToastType.success, text: res.message });

            // ✅ Reset file input
            if (fileInputRef.current) fileInputRef.current.value = "";
          } else {
            if (res.non_existent_items?.length > 0) {
              setNonExistItems(res.non_existent_items);
              setShowNonExistItemsModal(true);
            } else {
              showToast({ type: ToastType.error, text: res.error });
            }

            // ✅ Reset file input
            if (fileInputRef.current) fileInputRef.current.value = "";
          }
        } catch (err) {
          showToast({
            type: ToastType.error,
            text: "File upload failed." + err,
          });
          console.error(err);

          if (fileInputRef.current) fileInputRef.current.value = "";
        } finally {
          if (fileInputRef.current) fileInputRef.current.value = "";
        }
      },
    });
  };

  const submit = async () => {
    setLoading(true);
    const res = await fetchPut(url + "/inward/" + id, data);
    if (res.success) {
      showToast({ type: ToastType.success, text: res.message });
      navigate.push(urls.npd);
    } else showToast({ type: ToastType.error, text: res.error });
    setLoading(false);
  };

  return (
    <>
      {showNonExistItemsModal && (
        <ModalDialog
          show={showNonExistItemsModal}
          title="Items Not Exists"
          onClose={() => setShowNonExistItemsModal(false)}
        >
          <div className="p-4">
            <pre className="bg-gray-100 p-2 rounded text-red-600 whitespace-pre-wrap">
              <div className="modal-content">
                <h2>Rejected Upload Product</h2>
                <ul>
                  {nonExistItems.map((id: any, index: any) => (
                    <li key={index}>{id}</li>
                  ))}
                </ul>
              </div>
            </pre>
          </div>
        </ModalDialog>
      )}

      <div className="mobile-shrink sticky top-0 mt-2 px-2 md:px-6 h-full">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xl font-bold border-l-4 border-myPrimaryColor pl-2">
            NPD
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-4 md:gap-10 mb-2">
          <button
            className={`text-lg font-bold pl-2 ${
              screen === "npd_details"
                ? "bg-myPrimaryColor text-white rounded-md p-1"
                : "text-myPrimaryColor"
            }`}
            onClick={() => {
              if (id) {
                setScreen("npd_details");
                navigate.push(`${urls.npd}/view/${id}`);
              }
            }}
          >
            NPD Details
          </button>
          <button
            className={`text-lg font-bold pl-2 ${
              screen === "inward"
                ? "bg-myPrimaryColor text-white rounded-md p-1"
                : "text-myPrimaryColor"
            }`}
            onClick={() => {
              if (id) {
                setScreen("inward");
                navigate.push(`${urls.npd}/inward/${id}`);
              }
            }}
          >
            Inward
          </button>
        </div>

        {data.status === NPDStatus.submitted && (
          <div>
            <div className="my-5">
              <div className="flex flex-wrap gap-2 items-center justify-between">
                <h3 className="text-sm font-bold">Inward Details</h3>
                <label
                  onClick={handleDownloadTemplate}
                  className="bg-myPrimaryColor text-white p-1 rounded-md cursor-pointer"
                >
                  Template
                </label>
                <div className="">
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept=".csv"
                    onChange={handleCSVUpload}
                    className="hidden"
                  />

                  {/* <label id="csvUpload"> */}
                  <button
                    onClick={handleButtonClick}
                    className="bg-myPrimaryColor text-white rounded-lg px-3 py-1.5 text-sm cursor-pointer duration-300 my-1"
                  >
                    Upload CSV
                  </button>
                  {/* </label> */}
                </div>
                {/* <button onClick={()=>{}} className="bg-myPrimaryColor text-white rounded-lg px-3 py-1.5 text-sm cursor-pointer duration-300 my-1">Upload</button> */}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 pb-24">
                {data?.inward_items?.map((comp, i) => {
                  return (
                    <InwardProductComponent
                      data={comp}
                      edit={false}
                      disabled={false}
                      key={comp.id}
                      onDelete={(d: any) => {
                        setData((o) => ({
                          ...o,
                          inward_items: [...(o.inward_items || [])].filter(
                            (i) => i.id !== d.id,
                          ),
                        }));
                      }}
                      onSubmit={(data: InwardItemModel): void => {
                        setData((o: NPDModel) => ({
                          ...o,
                          inward_items: [
                            ...(o.inward_items || []).map((it) => {
                              if (it.id === data.id) return data;
                              return it;
                            }),
                          ],
                        }));
                      }}
                      existingItems={existingInwardItems}
                      product={data.product}
                    />
                  );
                })}
                {data.status === NPDStatus.submitted && (
                  <InwardProductComponent
                    key={addNewId1}
                    onSubmit={function (data: InwardItemModel): void {
                      setData((o: NPDModel) => ({
                        ...o,
                        inward_items: [...(o.inward_items || []), data],
                      }));
                      setAddNewId1(Math.random() * 1000);
                    }}
                    edit={true}
                    disabled={false}
                    existingItems={existingInwardItems}
                    product={data.product}
                  />
                )}
              </div>
            </div>
            <div className="flex flex-wrap sticky bottom-0 justify-end my-3 md:my-5 gap-2 md:gap-4 px-2 md:px-5 py-3 md:py-5 bg-white">
              <button
                type="button"
                className="px-10 py-1 rounded-md bg-myPrimaryColor text-white text-sm"
                onClick={() => {
                  submit();
                }}
              >
                Submit
              </button>
              <button
                type="button"
                className="rounded-md border border-green-600 text-sm font-semibold bg-white text-black px-3 py-1"
                onClick={() => navigate.push(urls.npd)}
              >
                Quit
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default NPDInwardScreen;
